import { ref, runTransaction, set, update } from "firebase/database";
import { buildNavGridGraph, computeNavGridRoute, haversineMeters } from "./navGrid.js";
import {
  ACTIVITY_MODE_WALK,
  isActivityModeCompatible,
  normalizeActivityMode,
} from "./activityMode.js";

const DEFAULT_ENTRY_RADIUS_M = 5;
const DEFAULT_EXIT_RADIUS_M = 22;
const DEFAULT_MAX_OFF_PATH_M = 50;
const MIN_ATTEMPT_DURATION_MS = 8000;
const SEGMENT_ATTEMPTS_QUEUE_KEY = "trekkin_segments_attempts_ops_queue_v1";
const SEGMENT_ATTEMPTS_QUEUE_EVENT = "trekkin:segments-attempts-ops-queue-updated";

const toNumber = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const isOnlineNow = () => {
  try {
    return typeof navigator === "undefined" ? true : navigator.onLine !== false;
  } catch {
    return true;
  }
};

const isLikelyOfflineError = (err) => {
  if (!err) return !isOnlineNow();
  const msg = String(err?.message || err || "").toLowerCase();
  return (
    !isOnlineNow() ||
    msg.includes("network") ||
    msg.includes("offline") ||
    msg.includes("failed to fetch") ||
    msg.includes("unavailable") ||
    msg.includes("connection") ||
    msg.includes("disconnect")
  );
};

const makeAttemptId = (segmentId, startedAt, finishedAt) => {
  const sid = String(segmentId || "").replace(/[^a-zA-Z0-9_-]/g, "").slice(0, 48) || "segment";
  const s = Math.max(0, Math.floor(Number(startedAt || 0)));
  const f = Math.max(0, Math.floor(Number(finishedAt || 0)));
  return `att-${sid}-${s}-${f}`;
};

const normalizeAttemptsQueue = (raw) => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => ({
      opId: String(item?.opId || "").trim(),
      type: String(item?.type || "").trim(),
      attemptId: String(item?.attemptId || "").trim(),
      payload:
        item?.payload && typeof item.payload === "object" ? { ...item.payload } : null,
      createdAt: Number(item?.createdAt || 0),
      updatedAt: Number(item?.updatedAt || 0),
    }))
    .filter((item) => item.opId && item.type === "persist_attempt" && item.attemptId && item.payload);
};

const readAttemptsQueue = () => {
  try {
    const raw = localStorage.getItem(SEGMENT_ATTEMPTS_QUEUE_KEY);
    if (!raw) return [];
    return normalizeAttemptsQueue(JSON.parse(raw));
  } catch {
    return [];
  }
};

const writeAttemptsQueue = (queue) => {
  const normalized = normalizeAttemptsQueue(queue);
  try {
    if (!normalized.length) localStorage.removeItem(SEGMENT_ATTEMPTS_QUEUE_KEY);
    else localStorage.setItem(SEGMENT_ATTEMPTS_QUEUE_KEY, JSON.stringify(normalized));
  } catch {}
  try {
    window.dispatchEvent(
      new CustomEvent(SEGMENT_ATTEMPTS_QUEUE_EVENT, { detail: { queue: normalized } })
    );
  } catch {}
  return normalized;
};

const enqueueSegmentAttempt = (payload) => {
  const now = Date.now();
  const attemptId = String(payload?.attemptId || "").trim();
  if (!attemptId) return { queued: false, attemptId: null };
  const queue = readAttemptsQueue();
  const existing = queue.find((item) => item.attemptId === attemptId);
  if (existing) {
    existing.payload = { ...(existing.payload || {}), ...(payload || {}) };
    existing.updatedAt = now;
    writeAttemptsQueue(queue);
    return { queued: true, attemptId };
  }
  queue.push({
    opId: `seg-attempt-${attemptId}`,
    type: "persist_attempt",
    attemptId,
    payload: { ...(payload || {}) },
    createdAt: now,
    updatedAt: now,
  });
  writeAttemptsQueue(queue);
  return { queued: true, attemptId };
};

const pointToSegmentDistanceMeters = (point, a, b) => {
  const pLat = Number(point?.lat);
  const pLon = Number(point?.lon ?? point?.lng);
  const aLat = Number(a?.lat);
  const aLon = Number(a?.lon ?? a?.lng);
  const bLat = Number(b?.lat);
  const bLon = Number(b?.lon ?? b?.lng);
  if (
    !Number.isFinite(pLat) ||
    !Number.isFinite(pLon) ||
    !Number.isFinite(aLat) ||
    !Number.isFinite(aLon) ||
    !Number.isFinite(bLat) ||
    !Number.isFinite(bLon)
  ) {
    return Number.POSITIVE_INFINITY;
  }
  const meanLatRad = ((pLat + aLat + bLat) / 3) * (Math.PI / 180);
  const metersPerDegLat = 111132.92;
  const metersPerDegLon = Math.max(1e-6, 111320 * Math.cos(meanLatRad));
  const ax = (aLon - pLon) * metersPerDegLon;
  const ay = (aLat - pLat) * metersPerDegLat;
  const bx = (bLon - pLon) * metersPerDegLon;
  const by = (bLat - pLat) * metersPerDegLat;
  const abx = bx - ax;
  const aby = by - ay;
  const denom = abx * abx + aby * aby;
  if (denom <= 1e-6) return Math.hypot(ax, ay);
  const t = Math.max(0, Math.min(1, -((ax * abx + ay * aby) / denom)));
  const cx = ax + t * abx;
  const cy = ay + t * aby;
  return Math.hypot(cx, cy);
};

const minDistanceToPathMeters = (point, path = []) => {
  if (!Array.isArray(path) || path.length < 2) return Number.POSITIVE_INFINITY;
  let best = Number.POSITIVE_INFINITY;
  for (let i = 1; i < path.length; i += 1) {
    const d = pointToSegmentDistanceMeters(point, path[i - 1], path[i]);
    if (d < best) best = d;
  }
  return best;
};

export function normalizeSegmentDefinitions(rawDefs, navGrid, options = {}) {
  const activeMode = options?.activityMode || null;
  const defs = Object.entries(rawDefs || {}).map(([id, value]) => ({
    id: String(id || "").trim(),
    ...(value || {}),
  }));
  let nodesById = null;
  return defs
    .filter(
      (s) =>
        s.id &&
        s.active !== false &&
        (!activeMode || isActivityModeCompatible(s.activityMode, activeMode))
    )
    .map((s) => {
      const startNodeId = String(s.startNodeId || "").trim();
      const endNodeId = String(s.endNodeId || "").trim();
      let startNode = null;
      let endNode = null;
      const startLat = Number(s.startLat);
      const startLon = Number(s.startLon);
      const endLat = Number(s.endLat);
      const endLon = Number(s.endLon);
      if (
        Number.isFinite(startLat) &&
        Number.isFinite(startLon) &&
        Number.isFinite(endLat) &&
        Number.isFinite(endLon)
      ) {
        startNode = { lat: startLat, lon: startLon };
        endNode = { lat: endLat, lon: endLon };
      } else {
        // Fallback de compatibilidade para segmentos antigos sem coordenadas.
        if (!nodesById) {
          try {
            const graph = buildNavGridGraph(navGrid || {});
            nodesById = graph?.nodesById || {};
          } catch {
            nodesById = {};
          }
        }
        startNode = nodesById[startNodeId] || null;
        endNode = nodesById[endNodeId] || null;
      }
      if (!startNode || !endNode) return null;
      const rawPath = Array.isArray(s.path) ? s.path : [];
      let path = rawPath
        .map((p) => ({
          lat: Number(p?.lat),
          lon: Number(p?.lon),
          ele: Number.isFinite(Number(p?.ele)) ? Number(p.ele) : undefined,
        }))
        .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lon));
      if (path.length < 2) {
        try {
          const route = computeNavGridRoute(
            navGrid || {},
            { lat: Number(startNode.lat), lon: Number(startNode.lon) },
            { lat: Number(endNode.lat), lon: Number(endNode.lon) },
            { preference: "shortest" }
          );
          if (route?.ok && Array.isArray(route.path) && route.path.length >= 2) {
            path = route.path
              .map((p) => ({
                lat: Number(p?.lat),
                lon: Number(p?.lon),
                ele: Number.isFinite(Number(p?.ele)) ? Number(p.ele) : undefined,
              }))
              .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lon));
          }
        } catch {}
      }
      if (path.length < 2) {
        path = [
          { lat: Number(startNode.lat), lon: Number(startNode.lon) },
          { lat: Number(endNode.lat), lon: Number(endNode.lon) },
        ];
      }
      return {
        id: s.id,
        name: String(s.name || "Segmento").trim() || "Segmento",
        startNodeId,
        endNodeId,
        start: { lat: Number(startNode.lat), lon: Number(startNode.lon) },
        end: { lat: Number(endNode.lat), lon: Number(endNode.lon) },
        path,
        distanceMeters: Math.max(0, toNumber(s.distanceMeters, 0)),
        ascentMeters: Math.max(0, toNumber(s.ascentMeters, 0)),
        descentMeters: Math.max(0, toNumber(s.descentMeters, 0)),
        xpFirstPlace: Math.max(0, Math.floor(toNumber(s.xpFirstPlace, 0))),
        activityMode: normalizeActivityMode(s.activityMode, ACTIVITY_MODE_WALK),
        createdAt: toNumber(s.createdAt, 0),
        createdByUid: s.createdByUid || null,
      };
    })
    .filter(Boolean);
}

export function detectSegmentTransition(position, defs, activeAttempt) {
  const lat = Number(position?.lat);
  const lon = Number(position?.lon ?? position?.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return { type: "none" };
  const point = { lat, lon };

  if (!activeAttempt) {
    for (const seg of defs || []) {
      const d = haversineMeters(point, seg.start);
      if (d <= DEFAULT_ENTRY_RADIUS_M) {
        return { type: "enter", segment: seg, point };
      }
    }
    return { type: "none" };
  }

  const seg = defs.find((item) => item.id === activeAttempt.segmentId);
  if (!seg) return { type: "cancel" };
  const offPathMeters = minDistanceToPathMeters(point, seg.path);
  if (Number.isFinite(offPathMeters) && offPathMeters > DEFAULT_MAX_OFF_PATH_M) {
    return { type: "cancel_offpath", segment: seg, point, offPathMeters };
  }
  const dEnd = haversineMeters(point, seg.end);
  if (dEnd <= DEFAULT_EXIT_RADIUS_M) {
    return { type: "finish", segment: seg, point };
  }
  return { type: "progress", segment: seg, point };
}

async function persistSegmentAttemptOnline({
  db,
  uid,
  userName,
  userSex,
  segment,
  startedAt,
  finishedAt,
}) {
  const safeUid = String(uid || "").trim();
  const segmentId = String(segment?.id || "").trim();
  if (!db || !safeUid || !segmentId) return { ok: false, reason: "invalid" };
  const durationMs = Math.max(0, toNumber(finishedAt, 0) - toNumber(startedAt, 0));
  if (durationMs < MIN_ATTEMPT_DURATION_MS) return { ok: false, reason: "too_short" };
  const durationSec = Number((durationMs / 1000).toFixed(1));
  const sex = String(userSex || "").toLowerCase();
  const sexNorm = sex === "male" || sex === "female" ? sex : "unknown";
  const name = String(userName || safeUid).trim() || safeUid;
  const now = Date.now();
  const xpFirstPlace = Math.max(0, Math.floor(toNumber(segment?.xpFirstPlace, 0)));

  const attemptId = makeAttemptId(segmentId, startedAt, finishedAt);
  const attempt = {
    id: attemptId,
    uid: safeUid,
    segmentId,
    segmentName: segment.name || "Segmento",
    startedAt: toNumber(startedAt, now),
    finishedAt: toNumber(finishedAt, now),
    durationMs,
    durationSec,
    sex: sexNorm,
    userName: name,
    createdAt: now,
  };

  await set(ref(db, `segments/attempts/${safeUid}/${attemptId}`), attempt);

  await update(ref(db), {
    [`segments/users/${safeUid}/stats/${segmentId}/segmentId`]: segmentId,
    [`segments/users/${safeUid}/stats/${segmentId}/segmentName`]: segment.name || "Segmento",
    [`segments/users/${safeUid}/stats/${segmentId}/lastDurationSec`]: durationSec,
    [`segments/users/${safeUid}/stats/${segmentId}/lastAt`]: toNumber(finishedAt, now),
  });

  await runTransaction(ref(db, `segments/users/${safeUid}/stats/${segmentId}`), (current) => {
    const cur = current && typeof current === "object" ? { ...current } : {};
    if (String(cur.lastAttemptId || "") === attemptId) return cur;
    const prevBest = toNumber(cur.bestDurationSec, 0);
    const nextAttempts = Math.max(0, toNumber(cur.attemptsCount, 0)) + 1;
    const nextBest = prevBest > 0 ? Math.min(prevBest, durationSec) : durationSec;
    return {
      ...cur,
      segmentId,
      segmentName: segment.name || "Segmento",
      bestDurationSec: Number(nextBest.toFixed(1)),
      lastDurationSec: durationSec,
      attemptsCount: nextAttempts,
      lastAttemptId: attemptId,
      lastAt: toNumber(finishedAt, now),
      updatedAt: now,
    };
  });

  let firstPlaceAward = null;
  let finalOverallRank = null;
  await runTransaction(ref(db, `segments/leaderboards/${segmentId}`), (current) => {
    const cur = current && typeof current === "object" ? { ...current } : {};
    const bestByUser = cur.bestByUser && typeof cur.bestByUser === "object" ? { ...cur.bestByUser } : {};
    const prev = bestByUser[safeUid] || {};
    const prevBest = toNumber(prev.bestDurationSec, 0);
    const nextBest = prevBest > 0 ? Math.min(prevBest, durationSec) : durationSec;
    bestByUser[safeUid] = {
      uid: safeUid,
      name,
      sex: sexNorm,
      bestDurationSec: Number(nextBest.toFixed(1)),
      updatedAt: now,
    };
    const rows = Object.values(bestByUser)
      .filter((row) => toNumber(row?.bestDurationSec, 0) > 0)
      .sort((a, b) => toNumber(a.bestDurationSec) - toNumber(b.bestDurationSec))
      .slice(0, 200);
    const maleRows = rows.filter((r) => String(r.sex || "") === "male");
    const femaleRows = rows.filter((r) => String(r.sex || "") === "female");
    finalOverallRank = rows.findIndex((r) => String(r?.uid || "") === safeUid) + 1;
    const prevOverallUid = String(cur?.overallTop?.[0]?.uid || "");
    const nextOverallUid = String(rows?.[0]?.uid || "");
    const transitioned = !!nextOverallUid && nextOverallUid !== prevOverallUid;
    if (transitioned && xpFirstPlace > 0) {
      firstPlaceAward = {
        uid: nextOverallUid,
        xp: xpFirstPlace,
        at: now,
      };
    }
    return {
      ...cur,
      segmentId,
      segmentName: segment.name || "Segmento",
      xpFirstPlace,
      bestByUser,
      overallTop: rows,
      maleTop: maleRows,
      femaleTop: femaleRows,
      kingUid: maleRows[0]?.uid || null,
      queenUid: femaleRows[0]?.uid || null,
      kingName: maleRows[0]?.name || null,
      queenName: femaleRows[0]?.name || null,
      updatedAt: now,
    };
  });

  if (firstPlaceAward?.uid && firstPlaceAward?.xp > 0) {
    const winnerUid = String(firstPlaceAward.uid);
    const xp = Math.max(0, Math.floor(toNumber(firstPlaceAward.xp, 0)));
    if (xp > 0) {
      await runTransaction(ref(db, `gamification/users/${winnerUid}`), (current) => {
        const cur = current && typeof current === "object" ? { ...current } : {};
        const xpTotal = Math.max(0, Math.floor(toNumber(cur.xpTotal, 0)));
        const awards = cur.segmentFirstPlaceAwards && typeof cur.segmentFirstPlaceAwards === "object"
          ? { ...cur.segmentFirstPlaceAwards }
          : {};
        awards[segmentId] = {
          segmentId,
          segmentName: segment.name || "Segmento",
          xp,
          at: now,
        };
        return {
          ...cur,
          xpTotal: xpTotal + xp,
          segmentFirstPlaceAwards: awards,
          updatedAt: now,
        };
      });
    }
  }

  return {
    ok: true,
    attempt,
    rankOverall: finalOverallRank > 0 ? finalOverallRank : null,
  };
}

export async function flushSegmentAttemptsQueue(dbArg = null) {
  if (!isOnlineNow()) {
    return { ok: false, reason: "offline", applied: 0, pending: readAttemptsQueue().length };
  }
  const dbToUse = dbArg || db;
  let queue = readAttemptsQueue();
  let applied = 0;
  while (queue.length) {
    const op = queue[0];
    try {
      await persistSegmentAttemptOnline({ ...(op.payload || {}), db: dbToUse });
      queue = queue.slice(1);
      writeAttemptsQueue(queue);
      applied += 1;
    } catch (err) {
      return {
        ok: false,
        reason: isLikelyOfflineError(err) ? "offline_during_flush" : "flush_error",
        applied,
        pending: queue.length,
        err: err?.message || String(err),
      };
    }
  }
  return { ok: true, applied, pending: 0 };
}

let segmentAttemptsAutoFlushInitialized = false;
let segmentAttemptsAutoFlushRunning = false;

export function initSegmentAttemptsAutoFlush() {
  if (segmentAttemptsAutoFlushInitialized || typeof window === "undefined") return;
  segmentAttemptsAutoFlushInitialized = true;
  const run = async () => {
    if (segmentAttemptsAutoFlushRunning) return;
    if (!isOnlineNow()) return;
    segmentAttemptsAutoFlushRunning = true;
    try {
      await flushSegmentAttemptsQueue(db);
    } catch {
      // no-op
    } finally {
      segmentAttemptsAutoFlushRunning = false;
    }
  };
  window.addEventListener("online", run);
  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") run();
  });
  window.addEventListener(SEGMENT_ATTEMPTS_QUEUE_EVENT, run);
  window.setTimeout(run, 1800);
}

export async function persistSegmentAttempt(params) {
  const dbToUse = params?.db || db;
  const safeUid = String(params?.uid || "").trim();
  const segmentId = String(params?.segment?.id || "").trim();
  const startedAt = toNumber(params?.startedAt, 0);
  const finishedAt = toNumber(params?.finishedAt, 0);
  const attemptId = makeAttemptId(segmentId, startedAt, finishedAt);
  const payload = { ...(params || {}), db: dbToUse, attemptId };

  if (!safeUid || !segmentId || !startedAt || !finishedAt) {
    return { ok: false, reason: "invalid" };
  }
  if (!isOnlineNow()) {
    enqueueSegmentAttempt(payload);
    return { ok: true, queued: true, attempt: { id: attemptId } };
  }
  try {
    return await persistSegmentAttemptOnline(payload);
  } catch (err) {
    if (isLikelyOfflineError(err)) {
      enqueueSegmentAttempt(payload);
      return { ok: true, queued: true, attempt: { id: attemptId } };
    }
    throw err;
  }
}
